import React, { useState } from 'react';
import { Dropdown } from 'antd';
import { MailOutlined, DownOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import BaseButton from 'components/atoms/baseButton';
import {
  useSmsButtons,
  useSendSms,
  useDoctorSendSms,
} from 'infrastructure/hooks/control-panel';
import { useAccess } from 'infrastructure/hooks/access/useAccess';
import { isRpmModule as isRpmModuleFunction } from 'infrastructure/functions';
import useUser from 'utils/useUser';
import SmsModal from 'components/templates/rpm/patients/patient/messages/modal';
import SmsDoctorModal from 'components/organisms/controlPanel/smsDoctorModal';
import { useSendScheduledMessage } from 'infrastructure/hooks/smsDashboard/useSendScheduledMessage';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { useAvailabilityAlert } from 'components/molecules/smsDashboard/availabilityAlert/use-availability-alert';
import { PlatformEnums, type ModuleType } from 'infrastructure/enums';

import styles from './styles.module.scss';

import type { MenuProps } from 'antd';
import type { ISendSmsDataProps } from 'infrastructure/hooks/control-panel/api';

interface Props {
  patientGuid: string;
  module: ModuleType;
}

const SmsDropdownButton: React.FC<Props> = ({ patientGuid, module }) => {
  const { t } = useTranslation();
  const { userType } = useUser();

  const [currentModule, setCurrentModule] = useState<ModuleType | null>(null);
  const [smsPopupOpen, setSmsPopupOpen] = useState(false);
  const [doctorSmsPopupOpen, setDoctorSmsPopupOpen] = useState(false);

  const {
    patientInfo,
    doctorInfo,
    isRpmModule,
    isCombinedModule,
    isBlockRpmModule,
    isBlockCcmModule,
    isCcmActionDisabled,
  } = useAccess({ module, patientGuid });

  const smsButtons = useSmsButtons({
    isAdminPanel: false,
    setSmsPopupOpen,
    userType,
    doctorWorkNumber: doctorInfo?.workNumber ?? undefined,
    setDoctorSmsPopupOpen,
    setCurrentModule,
  });

  const { checkAvailability, isLoading, Alert } = useAvailabilityAlert({
    patientGuid,
    withAlert: true,
  });
  const { sendSms, smsLoading } = useSendSms();
  const { sendScheduledMessage, sendScheduledMessageLoading } =
    useSendScheduledMessage(
      {},
      {
        onSuccess() {
          notificationController.info({
            message: t(
              'smsDashboard.message_scheduled_for_closest_available_time',
            ),
          });
        },
      },
    );
  const { sendDoctorSms, doctorSmsLoading } = useDoctorSendSms();

  const loading = isLoading || smsLoading || sendScheduledMessageLoading;

  const handleSubmit = async (data: ISendSmsDataProps) => {
    const patientPhone = data.isPrimaryNumber
      ? patientInfo?.loginInfo?.phone
      : patientInfo?.homeNumber;
    const { sendNow, sendLater, nextAvailableTime } = await checkAvailability(
      patientPhone!,
    );

    if (sendNow) {
      const res = await sendSms(data);
      return res;
    }

    if (sendLater && patientPhone && nextAvailableTime) {
      const res = await sendScheduledMessage({
        message: data.message,
        from: data.isRpmPanel ? PlatformEnums.RPM : PlatformEnums.CCM,
        patientPhone,
        scheduledTime: nextAvailableTime,
        patientGuid,
      });
      return res;
    }

    if (!sendNow && !sendLater) return { error: 'close alert' };

    return { error: null };
  };

  const buildMenuItems = (
    buttons: typeof smsButtons,
    isRpm: boolean,
    keyPrefix: string,
  ): MenuProps['items'] =>
    buttons.map((btn, i) => ({
      key: `${keyPrefix}-${i}`,
      label: btn.label,
      onClick: () => btn.handleClick?.(isRpm),
    }));

  const buildGroupedMenuItems = (
    buttons: typeof smsButtons,
    isRpm: boolean,
    keyPrefix: string,
    groupLabel: string,
  ): MenuProps['items'] => [
    {
      type: 'group',
      key: `${keyPrefix}-group`,
      label: groupLabel,
      children: buildMenuItems(buttons, isRpm, keyPrefix),
    },
  ];

  const rpmUnavailable = isBlockRpmModule;
  const ccmUnavailable = isBlockCcmModule || isCcmActionDisabled;

  const rpmLabel = `${t('labels.rpm')} ${t('labels.sms')}`;
  const ccmLabel = `${t('labels.ccm')} ${t('labels.sms')}`;

  let menuItems: MenuProps['items'];

  if (isCombinedModule && !rpmUnavailable && !ccmUnavailable) {
    menuItems = [
      {
        key: 'rpm-sms',
        label: rpmLabel,
        popupClassName: 'call-dropdown-submenu',
        popupOffset: [4, -4],
        children: buildGroupedMenuItems(smsButtons, true, 'rpm', rpmLabel),
      },
      {
        key: 'ccm-sms',
        label: ccmLabel,
        popupClassName: 'call-dropdown-submenu',
        popupOffset: [4, -4],
        children: buildGroupedMenuItems(smsButtons, false, 'ccm', ccmLabel),
      },
    ];
  } else if (isCombinedModule && rpmUnavailable && !ccmUnavailable) {
    menuItems = buildGroupedMenuItems(smsButtons, false, 'ccm', ccmLabel);
  } else if (isCombinedModule && !rpmUnavailable && ccmUnavailable) {
    menuItems = buildGroupedMenuItems(smsButtons, true, 'rpm', rpmLabel);
  } else if (isCombinedModule) {
    menuItems = [
      {
        key: 'rpm-sms',
        label: rpmLabel,
        disabled: true,
        popupClassName: 'call-dropdown-submenu',
        popupOffset: [4, -8],
        children: buildGroupedMenuItems(smsButtons, true, 'rpm', rpmLabel),
      },
      {
        key: 'ccm-sms',
        label: ccmLabel,
        disabled: true,
        popupClassName: 'call-dropdown-submenu',
        popupOffset: [4, -8],
        children: buildGroupedMenuItems(smsButtons, false, 'ccm', ccmLabel),
      },
    ];
  } else if (isRpmModule) {
    menuItems = buildGroupedMenuItems(smsButtons, true, 'rpm', rpmLabel);
  } else {
    menuItems = buildGroupedMenuItems(smsButtons, false, 'ccm', ccmLabel);
  }

  return (
    <>
      <Dropdown
        menu={{ items: menuItems }}
        trigger={['click']}
        rootClassName={styles['call-dropdown-menu']}
      >
        <BaseButton
          label={t('labels.sms')}
          icon={<MailOutlined />}
          rightIcon={
            <div className={styles['arrow-icon']}>
              <DownOutlined />
            </div>
          }
          type="secondary"
          className={styles['action-button']}
        />
      </Dropdown>
      {smsPopupOpen && (
        <SmsModal
          isOpen={smsPopupOpen}
          toggle={() => {
            setSmsPopupOpen(false);
            setCurrentModule(null);
          }}
          patientGuid={patientGuid}
          loading={loading}
          submit={handleSubmit}
          isRpmPanel={
            (currentModule && isRpmModuleFunction(currentModule)) || isRpmModule
          }
        />
      )}
      <SmsDoctorModal
        isOpen={doctorSmsPopupOpen}
        toggle={setDoctorSmsPopupOpen}
        patientGuid={patientGuid}
        loading={doctorSmsLoading}
        submit={sendDoctorSms}
      />
      {Alert}
    </>
  );
};

export default SmsDropdownButton;
